﻿import { useTranslation } from "../localization/useTranslation.ts";
import { LegalEntityScope } from "./legalEntities/LegalEntities.types.ts";
import { useUrlProvider } from "../useUrlProvider.ts";
import { DotLegalBreadCrumbModel } from "@dotlegal/dotlegal-ui-components";
import { SelectableItem } from "../processingActivity/ProcessingActivity.types.ts";
import MentionInputRender from "../common/components/mentionInput/MentionInputRender.tsx";
import { isNullOrWhitespace } from "../common/stringOperations.ts";
import { createElement } from "react";

// The customer facing id is shown in parentheses after the name, the way contracts and systems do it. It is
// optional because a page renders its crumbs before the entity has loaded.
interface BreadCrumbLegalEntity extends SelectableItem {
    userFriendlyId?: number;
}

export function useLegalEntityBreadCrumbs(legalEntityScope: LegalEntityScope, legalEntity: BreadCrumbLegalEntity) {
    const { translateString } = useTranslation();
    const { getGroupEntitiesUrl, getLegalEntityUrl, getExternalLegalEntities } = useUrlProvider();
    const result: Array<DotLegalBreadCrumbModel> = [];

    const isGroupEntity = legalEntityScope === LegalEntityScope.GroupEntity;
    result.push({
        name: isGroupEntity ? translateString("groupCompanies") : translateString("external"),
        link: isGroupEntity ? getGroupEntitiesUrl() : getExternalLegalEntities(),
        tooltip: getLegalEntityBreadCrumbTooltip(legalEntity.description),
    });

    result.push({
        name: getLegalEntityCrumbName(legalEntity),
        link: getLegalEntityUrl(legalEntity.id),
        tooltip: getLegalEntityBreadCrumbTooltip(legalEntity.description),
    });

    return result;
}

function getLegalEntityCrumbName(legalEntity: BreadCrumbLegalEntity) {
    return legalEntity.userFriendlyId ? `${legalEntity.name} (${legalEntity.userFriendlyId})` : legalEntity.name;
}

function getLegalEntityBreadCrumbTooltip(description?: string) {
    return isNullOrWhitespace(description) ? undefined : createElement(MentionInputRender, { value: description!, linkColor: "white" });
}
